import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Alert, Badge, Card, CardBody, Col, Input, Row, Spinner } from 'reactstrap';
import MedDataTable from '../common/MedDataTable';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import apiBase from '../../apiConfig';
import { formatSelectedPeriod } from '../../helpers/testPeriod';
import { getAuthHeaders } from '../../helpers/utils';

const ALERTS_ENDPOINT = `${apiBase}/p-admin/alerts.php`;

const ALERT_GROUPS = {
  suitsidlar: {
    title: 'Suitsidlar',
    description: "Tanlangan davrda suitsid xavfi yuqori chiqqan talabalar ro'yxati.",
    resultLabel: 'Chuqurroq psixologik tashxis',
    tone: 'danger'
  },
  depressiyalilar: {
    title: 'Depressiyalilar',
    description: "Tanlangan davrda depressiya testi bo'yicha yuqori xavfdagi talabalar ro'yxati.",
    resultLabel: 'Yuqori depressiya xavfi',
    tone: 'danger'
  },
  melanxoliklar: {
    title: 'Melanxoliklar',
    description: "Tanlangan davrda temperament testi bo'yicha melanxoliklar ro'yxati.",
    resultLabel: 'Melanxolik',
    tone: 'warning'
  },
  bezovtalanish: {
    title: 'Bezovtalanish',
    description: "Tanlangan davrda juda yuqori bezovtalanish natijasi chiqqan talabalar ro'yxati.",
    resultLabel: 'Juda yuqori bezovtalanish',
    tone: 'danger'
  },
  nevrasteniya: {
    title: 'Nevrasteniya',
    description: "Tanlangan davrda yaqqol nevrasteniya qayd etilgan talabalar ro'yxati.",
    resultLabel: 'Yaqqol nevrasteniya',
    tone: 'danger'
  }
};

const formatPercent = value => {
  const numeric = Number(value || 0);

  if (Number.isNaN(numeric)) {
    return '0%';
  }

  return `${numeric.toFixed(numeric % 1 === 0 ? 0 : 1)}%`;
};

const AlertGroupPage = ({ match }) => {
  const slug = ((match && match.params && match.params.slug) || '').trim();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [activePeriodLabel, setActivePeriodLabel] = useState('');
  const [search, setSearch] = useState('');
  const [facultyFilter, setFacultyFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [pageSize, setPageSize] = useStoredTablePageSize();

  const fallbackGroup = ALERT_GROUPS[slug] || null;
  const requestConfig = useMemo(
    () => ({
      headers: getAuthHeaders()
    }),
    []
  );

  useEffect(() => {
    setSearch('');
    setFacultyFilter('all');
    setGroupFilter('all');
    setCourseFilter('all');
  }, [slug]);

  useEffect(() => {
    let active = true;

    if (!slug) {
      setPayload(null);
      setLoading(false);
      setActivePeriodLabel('');
      setBanner({
        color: 'warning',
        text: "Xavf guruhi tanlanmagan."
      });
      return undefined;
    }

    setLoading(true);
    setPayload(null);
    setBanner(null);
    setActivePeriodLabel('');

    const fetchGroup = async () => {
      try {
        const response = await axios.get(ALERTS_ENDPOINT, {
          ...requestConfig,
          params: {
            slug
          }
        });

        if (!active) {
          return;
        }

        if (response.data && response.data.success) {
          setPayload(response.data.data || null);
          setActivePeriodLabel(formatSelectedPeriod(response.data.period));
          return;
        }

        throw new Error((response.data && response.data.message) || "Xavf guruhi ma'lumotlari yuklanmadi.");
      } catch (error) {
        if (!active) {
          return;
        }

        const message =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          "Xavf guruhi ma'lumotlarini yuklashda xatolik yuz berdi.";

        setPayload(null);
        setBanner({
          color: 'danger',
          text: message
        });
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchGroup();

    return () => {
      active = false;
    };
  }, [requestConfig, slug]);

  const groupMeta = payload && payload.group ? payload.group : fallbackGroup;
  const groupTitle = (groupMeta && groupMeta.title) || 'Xavf guruhi';
  const groupDescription =
    (groupMeta && groupMeta.description) || "Tanlangan xavf guruhi bo'yicha talabalar ro'yxati.";
  const groupResultLabel = groupMeta && (groupMeta.result_label || groupMeta.resultLabel);
  const groupTone = (groupMeta && groupMeta.tone) || 'secondary';
  const students = (payload && payload.students) || [];
  const filters = (payload && payload.filters) || { faculties: [], groups: [], courses: [] };
  const stats = (payload && payload.stats) || {
    student_count: 0,
    faculty_count: 0,
    group_count: 0,
    passport_count: 0,
    passport_percent: 0
  };

  const filteredStudents = useMemo(() => {
    const query = search.trim().toLowerCase();

    return students.filter(student => {
      if (facultyFilter !== 'all' && student.faculty !== facultyFilter) {
        return false;
      }

      if (groupFilter !== 'all' && student.group !== groupFilter) {
        return false;
      }

      if (courseFilter !== 'all' && (student.course || '') !== courseFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      const haystack = [
        student.full_name,
        student.faculty,
        student.group,
        student.course,
        student.passport,
        student.result,
        String(student.talaba_id)
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [courseFilter, facultyFilter, groupFilter, search, students]);

  const isFilteringActive =
    search.trim() !== '' || facultyFilter !== 'all' || groupFilter !== 'all' || courseFilter !== 'all';

  const statCards = useMemo(
    () => [
      {
        title: 'Talabalar',
        value: stats.student_count,
        note: "xavf guruhidagi yozuvlar"
      },
      {
        title: 'Fakultetlar',
        value: stats.faculty_count,
        note: "qamrab olingan fakultet"
      },
      {
        title: 'Guruhlar',
        value: stats.group_count,
        note: "xavf aniqlangan guruh"
      },
      {
        title: "Pasport to'ldirish",
        value: formatPercent(stats.passport_percent),
        note: `${stats.passport_count} ta pasport mavjud`
      }
    ],
    [stats.faculty_count, stats.group_count, stats.passport_count, stats.passport_percent, stats.student_count]
  );

  const columns = useMemo(
    () => [
      {
        dataField: 'order',
        text: 'T/R',
        sort: true,
        classes: 'align-middle font-weight-semi-bold'
      },
      {
        dataField: 'full_name',
        text: 'Talaba',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <div className="psi-students-person">
            <div className="psi-students-person__avatar">{row.initials}</div>
            <div className="psi-students-person__copy">
              <strong>{value}</strong>
              <span>Talaba ID: {row.talaba_id}</span>
            </div>
          </div>
        )
      },
      {
        dataField: 'faculty',
        text: 'Fakultet',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <div>
            <div className="font-weight-semi-bold">{value || "Ko'rsatilmagan"}</div>
            <small className="text-600">{row.course ? `${row.course}-kurs` : "Kurs ko'rsatilmagan"}</small>
          </div>
        )
      },
      {
        dataField: 'group',
        text: 'Guruh',
        sort: true,
        classes: 'align-middle',
        formatter: value => <span className="psi-students-chip psi-students-chip--group">{value || "Ko'rsatilmagan"}</span>
      },
      {
        dataField: 'passport_status',
        text: 'Pasport',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <div>
            <Badge color={row.passport_filled ? 'success' : 'secondary'} pill>
              {value}
            </Badge>
            <div className="text-600 fs--1 mt-1">{row.passport || "Ma'lumot yo'q"}</div>
          </div>
        )
      },
      {
        dataField: 'result',
        text: 'Xolati',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <Badge color={row.tone || 'secondary'} pill>
            {value}
          </Badge>
        )
      }
    ],
    []
  );

  return (
    <>
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-start mb-4">
        <div className="mb-3 mb-lg-0">
          <div className="text-uppercase text-600 font-weight-bold fs--2 mb-2">
            Xavf guruhlari{groupMeta && groupMeta.title ? ` / ${groupMeta.title}` : ''}
          </div>
          <h3 className="mb-1">{groupTitle}</h3>
          <p className="text-600 mb-0">{groupDescription}</p>
        </div>

        <div className="d-flex flex-wrap justify-content-lg-end">
          {activePeriodLabel ? (
            <Badge pill className="psi-active-period-badge mr-2 mb-2">
              {activePeriodLabel}
            </Badge>
          ) : null}
          {groupResultLabel ? (
            <Badge color={groupTone} pill className="mb-2">
              {groupResultLabel}
            </Badge>
          ) : null}
        </div>
      </div>

      {banner ? (
        <Alert color={banner.color} className="mb-3">
          {banner.text}
        </Alert>
      ) : null}

      <Row className="mb-1">
        {statCards.map(card => (
          <Col md="6" xl="3" key={card.title} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="text-uppercase text-600 font-weight-bold fs--2 mb-2">{card.title}</div>
                <div className="h2 mb-1">{card.value}</div>
                <div className="text-600">{card.note}</div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm mb-3">
        <CardBody>
          <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-end mb-3">
            <div className="mb-3 mb-xl-0">
              <h5 className="mb-1">Ro'yxat va filtrlar</h5>
              <p className="text-600 mb-0">Eski sahifadagi xavf guruhi ro'yxati tanlangan davr bo'yicha shu yerda ko'rsatiladi.</p>
            </div>
            <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size mt-2 mt-xl-0" />
          </div>

          <Row form>
            <Col xl="5" className="mb-2">
              <Input
                value={search}
                onChange={({ target }) => setSearch(target.value)}
                placeholder="F.I.O., talaba ID, guruh, pasport..."
              />
            </Col>
            <Col md="4" xl="2" className="mb-2">
              <Input type="select" value={facultyFilter} onChange={({ target }) => setFacultyFilter(target.value)}>
                <option value="all">Barcha fakultetlar</option>
                {(filters.faculties || []).map(item => (
                  <option key={item.value || item.name} value={item.value || item.name}>
                    {item.name} ({item.count})
                  </option>
                ))}
              </Input>
            </Col>
            <Col md="4" xl="2" className="mb-2">
              <Input type="select" value={groupFilter} onChange={({ target }) => setGroupFilter(target.value)}>
                <option value="all">Barcha guruhlar</option>
                {(filters.groups || []).map(item => (
                  <option key={item.value || item.name} value={item.value || item.name}>
                    {item.name} ({item.count})
                  </option>
                ))}
              </Input>
            </Col>
            <Col md="4" xl="3" className="mb-2">
              <Input type="select" value={courseFilter} onChange={({ target }) => setCourseFilter(target.value)}>
                <option value="all">Barcha kurslar</option>
                {(filters.courses || []).map(item => (
                  <option key={item} value={item}>
                    {item}-kurs
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center flex-wrap mt-3">
            <Badge color="light" pill className="px-3 py-2">
              {filteredStudents.length} ta yozuv
            </Badge>
            <small className="text-600 mt-2 mt-sm-0">
              Pasport qamrovi: {stats.passport_count}/{stats.student_count} ({formatPercent(stats.passport_percent)})
            </small>
          </div>
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardBody>
          {loading ? (
            <div className="text-center py-6">
              <Spinner color="primary" className="mb-3" />
              <div className="text-600">Xavf guruhi ma'lumotlari yuklanmoqda...</div>
            </div>
          ) : (
            <MedDataTable
              data={filteredStudents}
              columns={columns}
              keyField="id"
              pageSize={pageSize}
              paginationResetKey={`${slug}-${search}-${facultyFilter}-${groupFilter}-${courseFilter}`}
              noDataIndication={
                <div className="text-center py-5 text-500">
                  {isFilteringActive
                    ? "Tanlangan filtr bo'yicha talaba topilmadi."
                    : activePeriodLabel
                    ? `${activePeriodLabel} bo'yicha bu xavf guruhida talaba topilmadi.`
                    : "Tanlangan filtr bo'yicha talaba topilmadi."}
                </div>
              }
              tableClasses="table table-hover fs--1 mb-0 med-dashboard-table"
              headerClasses="bg-200 text-900"
              wrapperClasses="table-responsive"
              defaultSorted={[
                {
                  dataField: 'order',
                  order: 'asc'
                }
              ]}
            />
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default AlertGroupPage;
